'use client';

import { useMemo, useState } from 'react';
import {
  DEFAULT_SVG_PATTERN,
  SVG_PATTERN_KINDS,
  buildPatternCssBackground,
  buildSvgPattern,
  buildSvgPatternPreview,
  sanitizeColor,
  type SvgPatternKind,
} from '@/lib/browser-svggen-extra-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserSvgPatternGenerator() {
  const [kind, setKind] = useState<SvgPatternKind>(DEFAULT_SVG_PATTERN.kind);
  const [background, setBackground] = useState(DEFAULT_SVG_PATTERN.background);
  const [foreground, setForeground] = useState(DEFAULT_SVG_PATTERN.foreground);
  const [tile, setTile] = useState(DEFAULT_SVG_PATTERN.tile);
  const [scale, setScale] = useState(DEFAULT_SVG_PATTERN.scale);
  const [strokeWidth, setStrokeWidth] = useState(DEFAULT_SVG_PATTERN.strokeWidth);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const options = useMemo(
    () => ({
      kind,
      background: sanitizeColor(background, '#0f172a'),
      foreground: sanitizeColor(foreground, '#38bdf8'),
      tile,
      scale,
      strokeWidth,
    }),
    [kind, background, foreground, tile, scale, strokeWidth],
  );

  const svg = useMemo(() => buildSvgPattern(options), [options]);
  const previewSvg = useMemo(() => buildSvgPatternPreview(options), [options]);
  const css = useMemo(() => buildPatternCssBackground(options), [options]);

  async function handleCopy(value: string, label: string) {
    try {
      await copyText(value);
      setCopyMessage(`${label} copied to clipboard.`);
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">SVG pattern</span>
        <h2 className="tool-live-title">SVG pattern generator</h2>
        <p className="tool-live-subtitle">
          Build a seamless, tileable SVG background pattern with dots, grids, lines, crosses,
          zigzags, or triangles. Copy the SVG or a ready-to-paste CSS background, all in your
          browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Pattern</span>
              <select onChange={(e) => setKind(e.target.value as SvgPatternKind)} value={kind}>
                {SVG_PATTERN_KINDS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="tool-live-field">
              <span>Background</span>
              <input
                onChange={(e) => setBackground(e.target.value)}
                type="color"
                value={sanitizeColor(background, '#0f172a')}
              />
            </label>
            <label className="tool-live-field">
              <span>Foreground</span>
              <input
                onChange={(e) => setForeground(e.target.value)}
                type="color"
                value={sanitizeColor(foreground, '#38bdf8')}
              />
            </label>
            <label className="tool-live-field">
              <span>Tile size: {tile}px</span>
              <input
                max={80}
                min={8}
                onChange={(e) => setTile(Number(e.target.value))}
                type="range"
                value={tile}
              />
            </label>
            <label className="tool-live-field">
              <span>Shape scale: {scale}%</span>
              <input
                max={40}
                min={1}
                onChange={(e) => setScale(Number(e.target.value))}
                type="range"
                value={scale}
              />
            </label>
            <label className="tool-live-field">
              <span>Stroke width: {strokeWidth}px</span>
              <input
                max={8}
                min={1}
                onChange={(e) => setStrokeWidth(Number(e.target.value))}
                type="range"
                value={strokeWidth}
              />
            </label>
          </div>

          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              onClick={() => handleCopy(svg, 'SVG')}
              type="button"
            >
              Copy SVG
            </button>
            <button
              className="button button-secondary"
              onClick={() => handleCopy(css, 'CSS background')}
              type="button"
            >
              Copy CSS
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <span
            style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}
          >
            Live preview
          </span>
          <div
            style={{
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              height: '220px',
              overflow: 'hidden',
            }}
            dangerouslySetInnerHTML={{ __html: previewSvg }}
          />
          <label className="tool-live-field tool-live-field-full" style={{ marginTop: '16px' }}>
            <span>SVG markup</span>
            <textarea readOnly rows={5} value={svg} />
          </label>
          <label className="tool-live-field tool-live-field-full">
            <span>CSS background</span>
            <textarea readOnly rows={4} value={css} />
          </label>
          <p className="tool-live-panel-note">
            Generated locally in your browser. Nothing is uploaded.
          </p>
        </section>
      </div>
    </div>
  );
}
